import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLUListElement} menuList - The main menu list element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuList'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  initSwiper = (container) => {
    if (!container) {
      return;
    }
    this.swiper = new window.Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 0,
      observer: true,
      observeParents: true,
      loop: true,
      navigation: {
        nextEl: '.tw-carousel-mega-menu .swiper-buttons .swiper-button-next',
        prevEl: '.tw-carousel-mega-menu .swiper-buttons .swiper-button-prev',
      },
      pagination: {
        el: container.querySelector('.swiper-pagination'),
        clickable: true,
        type: 'progressbar',
      },
    });
    this.swiper.update();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Get the closest button element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLButtonElement | null}
   */
  #getButtonElement(event) {
    if (!(event?.target instanceof Element)) return null;

    return event.target.closest('button');
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    const searchIcon = document.querySelector('header-actions search-button');
    const accountIcon = document.querySelector(
      'header-actions dialog-component',
    );

    if (summary) {
      summary.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        searchIcon?.classList.add('tw-fade-out');
        searchIcon?.classList.remove('tw-fade-in');
        accountIcon?.classList.add('tw-fade-out');
        accountIcon?.classList.remove('tw-fade-in');
        setTimeout(() => {
          trapFocus(details);
        }, 0);
      });
    }
  }
  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  openSubmenu(event) {
    const submenuTrigger = this.#getButtonElement(event);
    const submenu =
      submenuTrigger?.parentElement?.querySelector('[ref="submenu[]"]');
    const utilityMenu = document.getElementById('utility-menu');
    const submenus = this.querySelectorAll('[ref="menuitem"]');
    if (submenu && submenuTrigger) {
      this.initSwiper(
        submenu.querySelector('[class*="tw-carousel-mega-menu-swiper-"]'),
      );
      submenuTrigger?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        submenu?.classList.add('tw-submenu-open');
        utilityMenu?.classList?.add('tw-menu-open');
        submenus.forEach((item) => {
          item?.classList?.add('tw-menu-open');
        });
      });
    }
    return;
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  closeSubmenu(event) {
    const submenuTrigger = this.#getButtonElement(event);
    const submenu = submenuTrigger?.closest('[ref="submenu[]"]');
    const utilityMenu = document.getElementById('utility-menu');
    const submenus = this.querySelectorAll('[ref="menuitem"]');
    if (submenu && submenuTrigger) {
      if (this?.swiper) {
        this?.swiper?.destroy(true, true);
      }

      submenuTrigger?.setAttribute('aria-expanded', 'false');
      requestAnimationFrame(() => {
        submenu?.classList.remove('tw-submenu-open');
        utilityMenu?.classList?.remove('tw-menu-open');
        submenus.forEach((item) => {
          item?.classList?.remove('tw-menu-open');
          item.setAttribute('aria-expanded', 'false');
        });
      });
    }
    return;
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    const searchIcon = document.querySelector('header-actions search-button');
    const accountIcon = document.querySelector(
      'header-actions dialog-component',
    );
    const utilityMenu = document.getElementById('utility-menu');

    if (!summary) return;

    searchIcon?.classList.add('tw-fade-in');
    searchIcon?.classList.remove('tw-fade-out');
    accountIcon?.classList.add('tw-fade-in');
    accountIcon?.classList.remove('tw-fade-out');

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        const openSubmenus = this.querySelectorAll('.tw-submenu-open');
        const submenus = this.querySelectorAll('[ref="menuitem"]');
        openDetails.forEach(reset);
        openSubmenus.forEach(reset);
        submenus.forEach(reset);
        utilityMenu?.classList?.remove('tw-menu-open');
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener(
          'animationend',
          removeWillChangeOnAnimationEnd,
        );
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('tw-submenu-open');
  element.classList.remove('tw-menu-open');
  element.classList.remove('menu-open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
